import { useState, useCallback } from 'react';
import { StackOperations } from '../services/stackOperations';
import { getChildrenItems } from '../services/getChildrenItems';

export function useStackLogic(debug = false, stackRootId = null) {
    const [selectedItems, setSelectedItems] = useState([]);
    const [lastSelectedItem, setLastSelectedItem] = useState(null);
    const [flattenedItems, setFlattenedItems] = useState([]);
    const [cardDataMap, setCardDataMap] = useState({});
    const [selectedItemForDetail, setSelectedItemForDetail] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0); // NEW: Refresh trigger for UI updates
    const [expandedItems, setExpandedItems] = useState(new Set()); // NEW: Track expanded folders
    const [folderFilters, setFolderFilters] = useState(new Map()); // NEW: Per-folder filter state
    const [recentlyCreatedItems, setRecentlyCreatedItems] = useState(new Set()); // NEW: Track recently created items
    
    // REMOVED: dragCompletedSignal - no longer needed for local-first approach

    const stackOps = new StackOperations(debug, stackRootId);

    // NEW: Methods to manage expanded state
    const toggleExpanded = useCallback((itemId) => {
        setExpandedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    }, []);

    const isExpanded = useCallback((itemId) => {
        return expandedItems.has(itemId);
    }, [expandedItems]);

    // NEW: Per-folder filter management
    const toggleFolderFilter = useCallback((folderId) => {
        setFolderFilters(prev => {
            const newFilters = new Map(prev);
            const currentFilter = newFilters.get(folderId) || { active: false, text: '' };
            
            newFilters.set(folderId, {
                ...currentFilter,
                active: !currentFilter.active
            });
            
            return newFilters;
        });
    }, []);

    const updateFolderFilterText = useCallback((folderId, text) => {
        setFolderFilters(prev => {
            const newFilters = new Map(prev);
            const currentFilter = newFilters.get(folderId) || { active: true, text: '' };
            
            newFilters.set(folderId, {
                ...currentFilter,
                text
            });
            
            return newFilters;
        });
    }, []);

    const getFolderFilter = useCallback((folderId) => {
        return folderFilters.get(folderId) || { active: false, text: '' };
    }, [folderFilters]);

    // NEW: Recently created items management
    const markAsRecentlyCreated = useCallback((itemId) => {
        setRecentlyCreatedItems(prev => new Set(prev).add(itemId));
        
        // Remove from recently created after 10 seconds
        setTimeout(() => {
            setRecentlyCreatedItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(itemId);
                return newSet;
            });
        }, 10000);
    }, []);

    const isRecentlyCreated = useCallback((itemId) => {
        return recentlyCreatedItems.has(itemId);
    }, [recentlyCreatedItems]);
    const debugLog = useCallback((message, data) => {
        if (debug) {
            console.log(message, data);
            if (data) {
                alert(`DEBUG - ${message}:\n${typeof data === 'object' ?
                    Object.entries(data)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join('\n')
                    : data}`);
            } else {
                alert(`DEBUG - ${message}`);
            }
        }
    }, [debug]);

    // Helper to refresh a single item's data from database (kept for edge cases)
    const refreshItemData = useCallback(async (itemId) => {
        if (!itemId) return null;
        
        try {
            const currentData = cardDataMap[itemId];
            if (!currentData?.ed) {
                if (debug) console.log(`No parent (ed) found for item ${itemId}, skipping refresh`);
                return null;
            }
            
            const parentChildren = await getChildrenItems(currentData.ed);
            const freshItemData = parentChildren.find(child => child._id === itemId);
            
            if (freshItemData) {
                setCardDataMap(prev => ({
                    ...prev,
                    [itemId]: freshItemData
                }));
                
                if (debug) {
                    console.log(`Refreshed item ${itemId} data:`, freshItemData);
                }
                
                return freshItemData;
            }
            
            return null;
        } catch (error) {
            console.error(`Error refreshing item ${itemId}:`, error);
            return null;
        }
    }, [cardDataMap, debug]);

    const registerItem = useCallback((itemId, level, itemData) => {
        setFlattenedItems(prev => {
            const exists = prev.some(item => item.id === itemId);
            if (!exists) {
                return [...prev, { id: itemId, level }];
            }
            return prev;
        });

        if (itemId && itemData) {
            setCardDataMap(prev => {
                if (!prev[itemId] || JSON.stringify(prev[itemId]) !== JSON.stringify(itemData)) {
                    return {
                        ...prev,
                        [itemId]: itemData
                    };
                }
                return prev;
            });

            if (debug) {
                console.log(`Registered item ${itemId} with data:`, itemData);
            }
        }
    }, [debug]);

    // ENHANCED: Local-first refresh - trigger UI updates after state changes
    const refreshAfterOperation = useCallback((operationType, affectedItemIds = []) => {
        if (debug) {
            console.log(`Local refresh after ${operationType}:`, affectedItemIds);
        }

        // Clear flattened items to force re-registration with new data
        setFlattenedItems([]);

        // Trigger a refresh signal for components to respond to
        setRefreshTrigger(prev => prev + 1);

        if (debug) {
            console.log(`UI refreshed locally for ${operationType}`);
        }
    }, [debug]);

    const handleDuplicateItem = useCallback(async () => {
        if (selectedItems.length === 0) {
            return;
        }

        const itemToDuplicate = selectedItems[0];

        try {
            const result = await stackOps.duplicateItem(itemToDuplicate, cardDataMap);
            
            // LOCAL UPDATE: Add new item to cardDataMap immediately
            setCardDataMap(prev => ({
                ...prev,
                [result.newId]: result.newItemData
            }));

            // Update UI state and force refresh
            setSelectedItems([result.newId]);
            
            // Trigger UI refresh after a brief delay
            setTimeout(() => {
                refreshAfterOperation('duplicate', [result.newId]);
            }, 100);
        } catch (error) {
            console.error("Duplication failed:", error);
            alert(`Failed to duplicate item: ${error.message}`);
        }
    }, [selectedItems, cardDataMap, stackOps, refreshAfterOperation]);

    // ENHANCED: Local-first item creation with proper UI refresh
    const handleNewItem = useCallback((sourceId, title, customParentId = null) => () => {
        const parentId = customParentId || stackRootId;
        
        if (!parentId) {
            console.error("No parent ID available to create new item");
            alert("No parent available for new item. Stack root ID is missing.");
            return;
        }

        if (debug) {
            console.log(`Creating new item with sourceId: ${sourceId}, title: ${title}, parentId: ${parentId}`);
        }

        stackOps.createNewItem(sourceId, title, parentId)
            .then(result => {
                if (debug) {
                    console.log(`New item created: ${result.newId} under parent: ${result.parentId}`);
                }
                
                // LOCAL UPDATE: Add new item to cardDataMap immediately
                setCardDataMap(prev => ({
                    ...prev,
                    [result.newId]: result.newItemData
                }));

                // Mark as recently created for special display treatment
                markAsRecentlyCreated(result.newId);

                // Update UI state and force refresh
                setSelectedItems([result.newId]);
                setLastSelectedItem(result.newId);
                
                // Trigger UI refresh after a brief delay to ensure state is updated
                setTimeout(() => {
                    refreshAfterOperation('create', [result.newId]);
                }, 100);
            })
            .catch(error => {
                console.error("New item creation failed:", error);
                alert(`Failed to create new item: ${error.message}`);
            });
    }, [stackRootId, stackOps, debug, refreshAfterOperation]);

    // ENHANCED: Local-first selection with immediate data updates
    const handleSelect = useCallback(async (itemId, isShiftKey, isCtrlKey, showDetailView = false, onSelect = null) => {
        if (debug) {
            console.log("=== HANDLESELECT DEBUG ===");
            console.log("showDetailView:", showDetailView);
            console.log("itemId:", itemId);
            console.log("isShiftKey:", isShiftKey, "isCtrlKey:", isCtrlKey);
        }

        if (showDetailView && !isShiftKey && !isCtrlKey) {
            console.log("SHOULD SHOW DETAIL VIEW!");
            
            // Get item data from local cache first
            let itemData = cardDataMap[itemId];
            
            // Only refresh from database if absolutely necessary (data missing)
            if (!itemData) {
                const freshData = await refreshItemData(itemId);
                itemData = freshData || { _id: itemId };
                
                if (debug) {
                    console.log(`Fresh item data for detail view:`, itemData);
                }
            }
            
            setSelectedItemForDetail(itemData);
            setSelectedItems([itemId]);
            setLastSelectedItem(itemId);
            return;
        }

        if (isShiftKey && lastSelectedItem) {
            const startIdx = flattenedItems.findIndex(item => item.id === lastSelectedItem);
            const endIdx = flattenedItems.findIndex(item => item.id === itemId);

            if (startIdx >= 0 && endIdx >= 0) {
                const start = Math.min(startIdx, endIdx);
                const end = Math.max(startIdx, endIdx);

                const itemsInRange = flattenedItems.slice(start, end + 1).map(item => item.id);

                if (isCtrlKey) {
                    setSelectedItems(prev => {
                        const newSelection = [...prev];
                        itemsInRange.forEach(id => {
                            if (!newSelection.includes(id)) {
                                newSelection.push(id);
                            }
                        });
                        return newSelection;
                    });
                } else {
                    setSelectedItems(itemsInRange);
                }
            }
        } else if (isCtrlKey) {
            setSelectedItems(prev => {
                if (prev.includes(itemId)) {
                    return prev.filter(id => id !== itemId);
                } else {
                    return [...prev, itemId];
                }
            });
        } else {
            setSelectedItems([itemId]);
        }

        setLastSelectedItem(itemId);

        if (onSelect) {
            onSelect(itemId, isShiftKey, isCtrlKey);
        }
    }, [debug, cardDataMap, lastSelectedItem, flattenedItems, refreshItemData]);

    const handleBackToList = useCallback(() => {
        setSelectedItemForDetail(null);
    }, []);

    // ENHANCED: Local-first detail view changes
    const handleDetailIdChange = useCallback(async (event) => {
        const newDetailId = event.target.value;

        if (!selectedItemForDetail?._id) {
            console.error("No item selected for detail view");
            return;
        }

        if (debug) {
            console.log(`Changing view preference for ${selectedItemForDetail._id} to ${newDetailId}`);
        }

        try {
            // Save to database and get updated item data
            const result = await stackOps.updateViewPreference(selectedItemForDetail._id, newDetailId, cardDataMap);

            // LOCAL UPDATE: Update cardDataMap immediately
            setCardDataMap(prev => ({
                ...prev,
                [selectedItemForDetail._id]: result.updatedItemData
            }));

            // Update selected item for detail view
            setSelectedItemForDetail(result.updatedItemData);

            if (debug) {
                console.log(`Successfully updated view preference to ${newDetailId}`);
            }

        } catch (error) {
            console.error("Failed to save view preference:", error);
            alert(`Failed to save view preference: ${error.message}`);
        }
    }, [selectedItemForDetail, stackOps, cardDataMap, debug]);

    // ENHANCED: Local-first move operation with proper refresh
    const handleMoveItem = useCallback(async (draggedItemId, targetId, sourceParentId) => {
        try {
            const result = await stackOps.moveItem(draggedItemId, targetId, sourceParentId, cardDataMap);
            
            // LOCAL UPDATE: Update moved item in cardDataMap immediately
            setCardDataMap(prev => ({
                ...prev,
                [draggedItemId]: result.updatedItemData
            }));

            // Trigger UI refresh after a brief delay
            setTimeout(() => {
                refreshAfterOperation('move', [draggedItemId]);
            }, 100);

            if (debug) {
                console.log(`Successfully moved item ${draggedItemId} to ${targetId}`);
            }

            return result;
        } catch (error) {
            console.error("Move operation failed:", error);
            throw error;
        }
    }, [stackOps, cardDataMap, refreshAfterOperation, debug]);

    // ENHANCED: Local-first title update
    const handleUpdateTitle = useCallback(async (itemId, newTitle) => {
        try {
            const result = await stackOps.updateItemTitle(itemId, newTitle, cardDataMap);
            
            // LOCAL UPDATE: Update title in cardDataMap immediately
            setCardDataMap(prev => ({
                ...prev,
                [itemId]: result.updatedItemData
            }));

            // Update selected item for detail if it's the same item
            if (selectedItemForDetail?._id === itemId) {
                setSelectedItemForDetail(result.updatedItemData);
            }

            if (debug) {
                console.log(`Successfully updated title for ${itemId} to "${newTitle}"`);
            }

            return result;
        } catch (error) {
            console.error("Title update failed:", error);
            throw error;
        }
    }, [stackOps, cardDataMap, selectedItemForDetail, debug]);

    // ENHANCED: Local-first folder conversion
    const handleMakeFolder = useCallback(async (itemId) => {
        try {
            const result = await stackOps.makeItemFolder(itemId, cardDataMap);
            
            // LOCAL UPDATE: Update folder status in cardDataMap immediately
            setCardDataMap(prev => ({
                ...prev,
                [itemId]: result.updatedItemData
            }));

            if (debug) {
                console.log(`Successfully made item ${itemId} a folder`);
            }

            return result;
        } catch (error) {
            console.error("Make folder operation failed:", error);
            throw error;
        }
    }, [stackOps, cardDataMap, debug]);

    // REMOVED: refreshStackRoot - no longer needed
    // REMOVED: dragCompletedSignal - no longer needed

    // Programmatic methods for external control
    const selectItem = useCallback((itemId, showDetail = false) => {
        if (showDetail) {
            const itemData = cardDataMap[itemId] || { _id: itemId };
            setSelectedItemForDetail(itemData);
        }
        setSelectedItems([itemId]);
        setLastSelectedItem(itemId);
    }, [cardDataMap]);

    const selectDetailView = useCallback((itemId, viewId) => {
        const itemData = cardDataMap[itemId] || { _id: itemId };
        setSelectedItemForDetail(itemData);
        handleDetailIdChange({ target: { value: viewId } });
    }, [cardDataMap, handleDetailIdChange]);

    const clearSelection = useCallback(() => {
        setSelectedItems([]);
        setLastSelectedItem(null);
        setSelectedItemForDetail(null);
    }, []);

    return {
        // State
        selectedItems,
        lastSelectedItem,
        flattenedItems,
        cardDataMap,
        selectedItemForDetail,
        refreshTrigger,  // NEW: Expose refresh trigger
        expandedItems,   // NEW: Expose expanded state
        folderFilters,   // NEW: Expose folder filters
        recentlyCreatedItems, // NEW: Expose recently created items
        
        // Setters
        setSelectedItems,
        setLastSelectedItem,
        setFlattenedItems,
        setCardDataMap,
        setSelectedItemForDetail,
        
        // Enhanced methods (local-first)
        handleSelect,
        handleDuplicateItem,
        handleNewItem,
        handleMoveItem,        // NEW: Direct move handler
        handleUpdateTitle,     // NEW: Direct title update handler
        handleMakeFolder,      // NEW: Direct folder conversion handler
        registerItem,
        handleBackToList,
        handleDetailIdChange,
        debugLog,
        
        // NEW: Expanded state management
        toggleExpanded,
        isExpanded,
        
        // NEW: Per-folder filter management
        toggleFolderFilter,
        updateFolderFilterText,
        getFolderFilter,
        
        // NEW: Recently created items management
        markAsRecentlyCreated,
        isRecentlyCreated,
        
        // Legacy method (kept for compatibility)
        refreshAfterDrop: refreshAfterOperation,
        
        // Utility methods
        refreshItemData,
        
        // Programmatic methods
        selectItem,
        selectDetailView,
        clearSelection,
        
        // Operations instance
        stackOps
    };
}